import { Link } from 'react-router-dom';

function Privacy() {
  return (
    <div className="page-container">
      <Link to="/" className="back-button">← Back to Home</Link>

      <h1>Privacy Policy</h1>
      <div className="content-card">
        <p><em>Last Updated: September 28, 2026</em></p>

        <section>
          <p>
            This Privacy Policy describes how LangShazam ("we", "us", "our") collects, uses, and shares
            information when you use the website at{' '}
            <a href="https://www.langshazam.com">https://www.langshazam.com</a> (the "Service"). By using
            the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h3>1. Information We Collect</h3>

          <h4>1.1 Audio You Provide</h4>
          <p>
            When you tap the microphone, your browser captures audio and streams it over a secure
            connection to our servers, where it is processed by third-party providers we engage to
            transcribe speech and identify the language being spoken.
          </p>
          <p>
            <strong>Audio retention:</strong> after a session ends, audio and transcripts may be retained
            in encrypted storage for a limited period to monitor service quality and improve detection
            accuracy, after which they are automatically deleted. We do not sell, share, or use this
            content for advertising or profiling.
          </p>
          <p>
            <strong>Permissions:</strong> we only access your microphone after you grant explicit browser
            permission, which you can revoke at any time in your browser settings.
          </p>

          <h4>1.2 Technical Information Collected Automatically</h4>
          <ul>
            <li>IP address (for rate limiting and abuse prevention)</li>
            <li>Browser, operating system, and device type</li>
            <li>Pages visited and timestamps</li>
            <li>Anonymous in-product events via Vercel Web Analytics (mic start/outcome; Langle page view, listen, replay, guess, complete, share, help, stats, and group actions). These do not include audio or the detected language.</li>
            <li>Connection metadata such as session duration</li>
          </ul>

          <h4>1.3 Account (optional)</h4>
          <p>
            If you continue with Google, we receive your Google name, email, and profile
            photo. If you create an account with email and password, we store that email,
            a salted password hash, and whether the address is verified. We use this to
            save your Langle streak and friend groups across devices, and to send
            transactional mail (email verification and password reset). When you
            are signed in, LangShazam mic sessions you run are also linked to your account:
            detected language, live transcript, and a replayable recording appear on your account page.
            The language detector requires an account, so every mic session is linked to yours and can
            be deleted from your account page. When you create an account you accept our{' '}
            <Link to="/terms">Terms of Service</Link>; we record the version you accepted, the time, the
            network (IP) address and browser it came from, so we can show that the agreement was made.
            We do not post to Google or read your Gmail. You can keep playing Langle without an
            account; stats then stay in this browser only.
          </p>
          <p>
            You can delete your account yourself at any time from the{' '}
            <Link to="/account">account page</Link>. Deletion is immediate and permanent: it erases
            your account details, every detection session including the audio recordings and
            transcripts, and your Langle history, streaks and group memberships. We keep only the
            record that the Terms were accepted (version and time, with the IP address and browser
            removed) and anonymous aggregate game statistics.
          </p>

          <h4>1.4 Information You Provide Voluntarily</h4>
          <p>
            If you contact us by email or social media, we keep that correspondence so we can respond to
            you and follow up.
          </p>
        </section>

        <section>
          <h3>2. How We Use Information</h3>
          <ul>
            <li>To save an optional account (Google or email) so Langle streaks, groups, and signed-in LangShazam detections follow you to a new device.</li>
            <li>To send transactional email such as address verification, password reset links, and a one-time welcome pointing you to today’s Langle.</li>
            <li>To operate the Service.</li>
            <li>To improve detection accuracy and the user experience.</li>
            <li>To detect abuse, prevent fraud, and enforce our Terms.</li>
            <li>To respond to inquiries and provide customer support.</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </section>

        <section>
          <h3>3. Cookies and Advertising</h3>
          <p>
            LangShazam itself uses minimal first-party storage, only what is necessary to operate the
            site. We display ads through Google AdSense, which uses cookies and similar technologies to
            serve ads based on your prior visits to this and other websites.
          </p>
          <ul>
            <li>
              Google, as a third-party vendor, uses cookies (including the DART cookie) to serve ads on
              the Service.
            </li>
            <li>
              You may opt out of personalized advertising at{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                Google Ads Settings
              </a>{' '}
              or, for participating vendors,{' '}
              <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
                aboutads.info/choices
              </a>.
            </li>
            <li>
              Read more about Google's ad practices at{' '}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
                policies.google.com/technologies/ads
              </a>.
            </li>
          </ul>
        </section>

        <section>
          <h3>4. Third-Party Service Providers</h3>
          <p>
            We rely on third-party providers to operate the Service: AssemblyAI (real-time speech
            recognition and language identification of the audio you stream), Amazon Web Services
            (hosting, and storage of recordings and transcripts), Supabase (database hosting),
            Vercel (website hosting and privacy-friendly analytics), Google (sign-in, and AdSense for
            advertising), and an email delivery provider for transactional mail. These providers access
            your information only as necessary to perform their functions on our behalf and are
            contractually bound to safeguard it.
          </p>
        </section>

        <section>
          <h3>5. Data Retention</h3>
          <p>
            Account data, detection sessions (recordings and transcripts) and Langle history are kept
            for as long as your account exists, and are erased when you delete a session or your
            account. Terms-acceptance records are kept for as long as we may need to demonstrate that
            the agreement was made, anonymized once the account is deleted. Other personal information
            is retained only for as long as necessary for the purposes described in this policy, after
            which it is deleted or anonymized.
          </p>
        </section>

        <section>
          <h3>6. Your Rights</h3>

          <h4>6.1 European Economic Area (GDPR)</h4>
          <p>
            If you are in the EEA, UK, or Switzerland, you have the right to access, correct, delete, or
            port your personal data, restrict or object to processing, and withdraw consent. You can
            delete your account and its data yourself from the <Link to="/account">account page</Link>;
            for anything else, email{' '}
            <a href="mailto:support@langshazam.com">support@langshazam.com</a>. You also have the
            right to lodge a complaint with your local supervisory authority.
          </p>

          <h4>6.2 California Residents (CCPA / CPRA)</h4>
          <p>
            California residents have the right to know what personal information we collect, to request
            deletion, to correct inaccurate information, and to opt out of "sales" or "sharing" of
            personal information. We do not sell or share personal information for cross-context
            behavioral advertising. You can delete your account yourself from the{' '}
            <Link to="/account">account page</Link>; to exercise other CCPA rights, email{' '}
            <a href="mailto:support@langshazam.com">support@langshazam.com</a>.
          </p>
        </section>

        <section>
          <h3>7. Children's Privacy</h3>
          <p>
            LangShazam is not directed to children under 13, and we do not knowingly collect personal
            information from children under 13. If you believe a child has provided us with personal
            information, please contact us and we will delete it.
          </p>
        </section>

        <section>
          <h3>8. Security</h3>
          <p>
            We use industry-standard practices to protect your information, including encryption in
            transit and at rest, and access controls on internal systems. No method of transmission or
            storage is 100% secure, however, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h3>9. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. The "Last Updated" date at the top of
            this page reflects the most recent revision.
          </p>
        </section>

        <section>
          <h3>10. Contact Us</h3>
          <p>
            For questions or requests related to this Privacy Policy, contact us at{' '}
            <a href="mailto:support@langshazam.com">support@langshazam.com</a> or through our{' '}
            <Link to="/contact">Contact page</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default Privacy;
